import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { GEOLOCATION_TOKEN, GeolocationProvider, GlobalRefreshService, LocationAccessService } from '@core';
import { Observable, Subscriber, switchMap, take, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';

@Component({
  selector: 'lib-location-button',
  templateUrl: './location-button.component.html',
  styleUrls: ['./location-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AsyncPipe, IonicModule]
})
export class LocationButtonComponent {
  private readonly geolocationProvider: GeolocationProvider = inject(GEOLOCATION_TOKEN);
  private readonly globalRefreshService: GlobalRefreshService = inject(GlobalRefreshService);
  private readonly locationAccessService: LocationAccessService = inject(LocationAccessService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly clicked: OutputEmitterRef<void> = output<void>();

  readonly isPageLoaded: WritableSignal<boolean> = signal<boolean>(false);

  readonly isPageLoaded$: Observable<boolean> = new Observable<boolean>((subscriber: Subscriber<boolean>) => {
    setTimeout(() => subscriber.next(this.isPageLoaded()), 2000);
  });

  readonly isPermissionGranted$: Observable<boolean> = this.globalRefreshService.refresh$.pipe(
    switchMap(() => this.geolocationProvider.isPermissionGranted()),
    tap(() => this.isPageLoaded.set(true))
  );

  readonly isLocationEnabled$: Observable<boolean> = this.globalRefreshService.refresh$.pipe(
    switchMap(() => this.geolocationProvider.isLocationServicesEnabled()),
    tap(() => this.isPageLoaded.set(true))
  );

  openLocationFlow(): void {
    this.clicked.emit();

    this.locationAccessService.ensureLocation$().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
